import { Form, Modal, notification, Select, Button } from "antd";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import { updateGroupStudents, getGroupById } from "store/slices/groupsSlice";
import { getAvailableStudentSupportCourses } from "store/slices/supportCoursesSlice";

const UpdateStudentSupportCourseGroupModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form?.useForm();

  // Sélecteurs Redux
  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { loading, group } = useSelector((state) => state?.groups);
  const { availableStudentSupportCourses } = useSelector(
    (state) => state?.supportCourses
  );
  const [selectedState, setSelectedState] = useState(group?.groupState);

  // État du modal
  const open =
    isModalOpen && modalType === "update_support_course_group_students";
  const groupId = modalProps?.groupId;

  // Charger les étudiants disponibles et initialiser le formulaire
  useEffect(() => {
    if (open && group?.supportCourseId) {
      dispatch(
        getAvailableStudentSupportCourses({
          supportCourseId: group?.supportCourseId?._id,
          groupId: groupId,
        })
      );

      form?.setFieldsValue({
        groupState: group?.groupState,
      });
    }
  }, [dispatch, open, group, form, groupId]);
  console?.log("etudiants for suppCourse : ", availableStudentSupportCourses);

  // Pré-remplir la sélection des étudiants
  useEffect(() => {
    if (open && group?.students?.length) {
      // Récupérer les IDs des étudiants actuels du groupe
      const currentStudentIds = group?.students?.map(
        (s) => s?.studentSupportCourseId?._id
      );

      // Pré-sélectionner les étudiants actuels du groupe
      form?.setFieldsValue({
        students: currentStudentIds,
      });
    }
  }, [group?.students, form, open]);

  // Gérer la soumission du formulaire
  const onSubmit = async (values) => {
    try {
      const formattedValues = {
        groupState: values?.groupState,
        students: values?.students?.map((studentSupportCourseId) => ({
          studentSupportCourseId,
        })),
      };

      await dispatch(
        updateGroupStudents({
          groupId,
          data: formattedValues,
        })
      )?.unwrap();

      notification?.success({
        message: t("groups.crud.form.update.success"),
        duration: 3,
      });

      onClose();
      dispatch(getGroupById(groupId));
    } catch (error) {
      notification?.error({
        message: t("groups.crud.form.update.error"),
        description: error?.message || t("common.error.generic"),
        duration: 4,
      });
    }
  };

  const onClose = () => {
    form?.resetFields();
    dispatch(closeModal());
  };

  // Options pour le type de groupe
  const groupStateOptions =
    group?.groupeType === "online"
      ? ["particulier", "miniGroupe"]
      : ["particulier", "miniGroupe", "groupe"];

  // Validation du nombre d'étudiants
  const validateStudentsCount = (_, value) => {
    if (!value || !selectedState) return Promise?.resolve();

    const count = value?.length;

    switch (selectedState) {
      case "particulier":
        if (count !== 1) {
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.single")
          );
        }
        break;
      case "miniGroupe":
        if (count < 2 || count > 3) {
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.mini")
          );
        }
        break;
      case "groupe":
        if (count < 4) {
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.group")
          );
        }
        break;
      default:
        return Promise?.resolve();
    }

    return Promise?.resolve();
  };
  // Ajout d'un effet pour valider les étudiants quand l'état change
  useEffect(() => {
    if (selectedState) {
      // Revalider le champ students
      form?.validateFields(["students"])?.catch(() => {
        // Ignorer les erreurs de validation ici
      });
    }
  }, [selectedState, form]);

  return (
    <Modal
      title={t("groups.crud.form.update.student.title")}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("common.cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => form?.submit()}
          loading={loading}
        >
          {t("common.save")}
        </Button>,
      ]}
      destroyOnClose
      centered
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name="groupState"
          label={t("groups.crud.form.label.state")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.error.required.state"),
            },
          ]}
        >
          <Select
            placeholder={t("groups.crud.form.placeholder.state")}
            onChange={(value) => {
              setSelectedState(value);
            }}
          >
            {groupStateOptions?.map((state) => (
              <Select.Option key={state} value={state}>
                {t(`groups.state.${state?.replace(/\s+/g, "")?.toLowerCase()}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="students"
          label={t("groups.crud.form.label.students")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.error.required.students"),
            },
            {
              validator: validateStudentsCount,
              validateTrigger: ["onChange", "onBlur"],
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder={t("groups.crud.form.placeholder.students")}
            loading={loading}
            onChange={() => {
              // Revalider le champ immédiatement après un changement
              form?.validateFields(["students"])?.catch(() => {
                // Ignorer les erreurs de validation ici
              });
            }}
          >
            {availableStudentSupportCourses?.map((student) => (
              <Select.Option
                key={student?.studentSupportCourseId?._id}
                value={student?.studentSupportCourseId?._id}
              >
                {student?.studentSupportCourseId?.studentId?.fullName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UpdateStudentSupportCourseGroupModal;
